import { AnimatePresence } from "motion/react";
import { Suspense, lazy, useContext, useEffect } from "react";
import {
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import { FlocknoteAppState } from "Flocknote/state.js";

import MissingNetworkInfo from "Composer/MissingNetworkInfo";
import { MemberToolbarPortal } from "CrossFeatureComponents/MemberToolbar/MemberToolbarPortal";
import Campaigns from "Giving/Campaigns/CampaignsPage";
import PledgeUserList from "Giving/Campaigns/PledgeUserList";
import RecurringGivingList from "Giving/RecurringGivingList";
import IIGAddressPortal from "IIGAddress/IIGAddressPortal";
import SignupAddressPortal from "SignupExtendedFieldsAddress/SignupAddressPortal";

import { useQueryParams } from "Utilities/Hooks/useQueryParams";

import DefaultDocumentTitle from "./DefaultDocumentTitle";
import PrivateRoute from "./PrivateRoute";
import { useRefreshAfterIdleHook } from "./refreshAfterIdleHook";

const Dashboard = lazy(() => import("Dashboard"));
const UIKitPage = lazy(() => import("UI/UIKit/UIKitPage"));
const DevSettings = lazy(() => import("Utilities/DevSettings"));
const SignupsPublicPage = lazy(() => import("Signups/SignupsPublicPage"));
const FamilySinglePage = lazy(() =>
  import("Family/ConfirmationLandingPage/ConfirmationLandingPage")
);
const Playground = lazy(() => import("Playground"));
const Login = lazy(() => import("Login"));
const Status = lazy(() => import("Status"));
const SignupsInSystem = lazy(() => import("Signups/SignupsInSystem"));
const TextingBilling = lazy(() => import("Billing/TextingBilling"));
const UserProfile = lazy(() => import("UserProfile"));

const NewBoard = lazy(() => import("Boards/NewBoard"));
const BoardPage = lazy(() => import("Boards/BoardPage"));
const HappeTools = lazy(() => import("HappeTools/Tools"));
const HappeToolsDiosync = lazy(() => import("HappeTools/Diosync/Diosync"));
const HappePortal = lazy(() => import("Happe/Portal"));
const HappeSearchV2 = lazy(() => import("Happe/SearchV2"));
const BoardsIntroPage = lazy(() => import("Boards/IntroPage"));
const GivingPage = lazy(() => import("Giving/GivingPage"));
const GivingBatchesListPrintView = lazy(() =>
  import("Giving/BatchesList/PrintView")
);
const GivingBatchPrintView = lazy(() => import("Giving/Batch/PrintView"));
const GivingFundListPrintView = lazy(() => import("Giving/FundList/PrintView"));
const GivingCampaignsListPrintView = lazy(() =>
  import("Giving/CampaignsList/PrintView")
);
const GivingPledgesPrintView = lazy(() => import("Giving/Pledges/PrintView"));
const GivingTransactionListPrintView = lazy(() =>
  import("Giving/TransactionList/PrintView")
);
const LegacyUnassignedGiftsButton = lazy(() =>
  import("Giving/UnassignedGiftsReport/LegacyUnassignedGiftsButton")
);

const GivingHappeTools = lazy(() => import("Giving/HappeTools"));

const PaymentPage = lazy(() => import("Payments/PaymentPage"));
const EnrollmentPage = lazy(() => import("Payments/EnrollmentPage"));
// Points at the redesigned public pledge page (drop-in replacement for the old
// Giving/Pledging/PledgePage, which stays on disk as the revert path).
const PledgePage = lazy(() => import("Giving/Pledging/PledgePagePublic"));
const MagicNotesScraper = lazy(() => import("MagicNotes/Scraper"));
const SettingsPanels = lazy(() => import("Network/SettingsPanels"));
const TemplatePage = lazy(() => import("Composer/Template/TemplatePage"));
const TemplateConfigurationPage = lazy(() =>
  import("Composer/Template/TemplateConfigurationPage")
);
const AccountMergePage = lazy(() => import("AccountMerge/AccountMergePage"));
const AccountMergeModal = lazy(() => import("AccountMerge/AccountMergeModal"));
const DeterminePaymentPage = lazy(() =>
  import("Payments/FundItPage/DeterminePaymentPage")
);
const FunditReceiptPage = lazy(() => import("Payments/ReceiptPage"));
const JoinGroupLandingPage = lazy(() => import("Group/JoinGroupLandingPage"));
const MerchantCenterInstantAuth = lazy(() =>
  import("Payments/MerchantCenterInstantAuth")
);
const LegacyMyNotesLoadingCarousel = lazy(() =>
  import("LegacyMyNotes/LegacyMyNotesLoadingCarousel")
);
const ReplyToGiveInfo = lazy(() => import("Note/ReplyToGiveInfo"));
const ManualPaymentProcessing = lazy(() =>
  import("Payments/ManualPaymentProcessing")
);
const ScheduledNotesReport = lazy(() => import("Reports/ScheduledNotes"));
const FamilyRegistration = lazy(() => import("FamilyRegistration/PublicPage"));
const FamilyRegistrationAdminPage = lazy(() =>
  import("FamilyRegistration/AdminPage/FamilyRegistrationAdminPage")
);
const NumberManagement = lazy(() => import("NumberManagement"));
const SavedReports = lazy(() => import("Reports/SavedReports"));
const HouseholdDatabase = lazy(() => import("HouseholdDatabase"));
const PrayerRequests = lazy(() => import("PrayerRequests/PrayerRequests"));
const PublicSubmitPrayerRequest = lazy(() =>
  import(
    "PrayerRequests/components/PublicSubmitPrayerRequestPage/PublicSubmitPrayerRequestPage"
  )
);
const ContactsPage = lazy(() => import("Contacts/ContactsPage"));
const UnsubscribeContactPage = lazy(() => import("Contacts/UnsubscribePage"));
const ParishesPage = lazy(() => import("Parishes/ParishesPage"));
const HealthReportPage = lazy(() =>
  import("Parishes/ParishesPage/HealthReportPage")
);
const SacramentPage = lazy(() => import("SacramentTracking/SacramentPage"));
const SmartGroupFilterPanel = lazy(() => import("Group/SmartGroupFilterPanel"));

const AskTheMemberFamilyRelationships = lazy(() =>
  import("CrossFeatureComponents/AskTheMember/AskTheMemberFamilyRelationships")
);
const AskTheMemberDuplicates = lazy(() =>
  import("CrossFeatureComponents/AskTheMember/AskTheMemberDuplicates")
);
const AskTheMemberMedicalInfo = lazy(() =>
  import("CrossFeatureComponents/AskTheMember/AskTheMemberMedicalInfo")
);
const AskTheMemberEmergencyContact = lazy(() =>
  import("CrossFeatureComponents/AskTheMember/AskTheMemberEmergencyContact")
);
const SmartGroupPromoSplash = lazy(() => import("Group/SmartGroupPromoSplash"));
const HouseholdStatuses = lazy(() => import("HouseholdStatuses"));
const ExtendedFieldsPage = lazy(() =>
  import("ExtendedFields/ExtendedFieldsPage/ExtendedFieldsPage")
);
const GroupsManagement = lazy(() => import("GroupsManagement"));
const Library = lazy(() => import("Library"));
const SignupsListAndArchivePage = lazy(() =>
  import("Signups/SignupView/SignupsListAndArchivePage")
);
const DataCleanupPage = lazy(() => import("DataTools/DataCleanupPage"));
const Transfers = lazy(() => import("HappeTools/Transfers"));
const MemberPortalPublic = lazy(() => import("MemberPortal/Public"));
const MemberPortalAdmin = lazy(() => import("MemberPortal/Admin"));
const RESetup = lazy(() => import("RESetup"));

const ViewFiltersButtons = lazy(() =>
  import("Note/SendToFilters/ViewFiltersButtons")
);

const NON_REACT_URLS = [
  // "/note",
  // "/mynotes",
  "/unolytics",
  "/subnetworks",
  "/superunolytics",
  "/event",
];

const NON_REACT_GROUP_URLS = [
  "/:group",
  "/:group/people",
  "/:group/notes",
  "/:group/notes/*",
  "/:group/note/*",
  "/:group/user/*",
  "/:group/supertext-packages",
  "/:group/import",
];

// Legacy support: /:group/people/import/:importID was once handled by Backbone.
// We redirect to the contentKey URL so the Importing modal opens on a fresh page load.
const ImportRedirect = () => {
  const { groupURL, importID } = useParams();
  const redirectUrl = `/${groupURL}?contentKey=Importing&contentProps=${encodeURIComponent(
    JSON.stringify({ completedImportID: Number(importID) })
  )}`;

  useEffect(() => {
    window.location.replace(redirectUrl);
  }, [redirectUrl]);

  return null;
};

export default function Routes() {
  const isDevEnv = process.env.NODE_ENV === "development";
  useRefreshAfterIdleHook();

  // URL matchers for standalone modals
  const showTextingBilling = Boolean(useRouteMatch("*/supertext-packages"));
  const showUserProfile = Boolean(useRouteMatch("*/user/:userID"));

  // Query param matchers
  const params = useQueryParams();
  const showEditSmartGroup = Boolean(params.get("editSmartGroup"));

  const showAccountMergeModal = Boolean(
    useRouteMatch(["*/*/merge", "*/*/duplicate-profiles"])
  );

  const showSettingsPanels = Boolean(
    useRouteMatch([
      "*/settings/network",
      "*/settings/network-sharing",
      "*/settings/network-welcome",
      "*/settings/network-security",
      "/:groupURL/settings/group",
      "/:groupURL/settings/group-sharing",
      "/:groupURL/settings/group-welcome",
      "/:groupURL/*/settings/group",
      "/:groupURL/*/settings/group-sharing",
      "/:groupURL/*/settings/group-welcome",
      "*/settingspanel/fund",
      "/giving/settings/campaign",
      "*/settings/campaign",
    ])
  );
  const newBoardMatch = useRouteMatch([
    "/:groupURL/new-board", // for creating a new board immediately after navigating to a group
    "/:groupURL/*/new-board", // for creating a new board while on the People, Notes, or Boards Intro page
    "/:groupURL/board/*/new-board", // for creating a new board while on an existing board
  ]);
  const showMemberToolbar = useRouteMatch([
    "/note",
    "/user",
    "/network",
    "/completeProfile",
    "/me",
  ]);
  // React owns the Address widget on the IIG single-field page. The portal
  // self-gates on its holder node + the field being an Address; this route
  // match just keeps it from mounting anywhere else.
  const showIIGAddress = useRouteMatch("/user/updateField");
  // React owns the Address field(s) on the Simple Signup / completeMyProfile
  // multi-field page. The portal self-gates on its holder nodes +
  // window.data.extendedFields; this route match keeps it off other pages.
  const showSignupAddress = useRouteMatch(
    "/network/:networkID/completeMyProfile"
  );

  const [{ composer }] = useContext(FlocknoteAppState);

  return (
    <Suspense fallback>
      <DefaultDocumentTitle />
      <Switch>
        {/* Protected (Non-React) URLs */}
        <Route path={NON_REACT_URLS} component={null} />

        {/* Dev-Only URLs */}
        {isDevEnv ? <Route path="/ui" component={UIKitPage} /> : null}
        {isDevEnv ? <Route path="/dev" component={DevSettings} /> : null}
        {isDevEnv ? <Route path="/playground" component={Playground} /> : null}
        {isDevEnv ? (
          <Route path="/magicnotes" component={MagicNotesScraper} />
        ) : null}
        {isDevEnv ? (
          <Route path="/group-reorder-proof" component={GroupsManagement} />
        ) : null}

        {/* Login Page */}
        <Route path="/login" component={Login} />

        {/* Status */}
        <Route path="/queue" component={Status} />

        {/* Action Center */}
        {/* Note: action-center (sometimes) shows in background when billing is open */}
        <Route
          path={["/action-center", "/dashboard", "/billing"]}
          component={Dashboard}
        />

        <PrivateRoute
          path="/mynotes"
          component={LegacyMyNotesLoadingCarousel}
        />

        {/* Transfers Page */}
        <Route exact path="/transfer" component={Transfers} />
        <Route path="/transfer/:transferID" component={Transfers} />

        <Route path="/contacts" component={ContactsPage} />

        <PrivateRoute
          path={["/parishes", "/parishes/:subnetworkShortname"]}
          component={ParishesPage}
        />

        <PrivateRoute path="/healthreport" component={HealthReportPage} />

        {/* Family Confirmation */}
        <Route path="/familyconfirmation" component={FamilySinglePage} />

        {/* Signups (Public Page) */}
        <Route
          path="/signup/:signupListID/give/:fundID"
          component={SignupsPublicPage}
        />
        <Route path="/signup/:signupListID" component={SignupsPublicPage} />

        {/* Signups (Admin Pages) */}
        <PrivateRoute
          path="/signups/all"
          exact
          component={SignupsListAndArchivePage}
        />
        <PrivateRoute
          // accommodate URL-based modals/overlays
          path="/signups/all/*"
          component={SignupsListAndArchivePage}
        />
        <PrivateRoute
          path={[
            "/signups/:signupListID/:signupTab/:signupItemID/:signupItemTab",
            "/signups/:signupListID/:signupTab/:signupItemID",
            "/signups/:signupListID/:signupTab",
            "/signups/:signupListID",
            "/signups",
          ]}
          component={SignupsInSystem}
        />

        {/* Member Portal - Admin */}
        <PrivateRoute path="/homepage" component={MemberPortalAdmin} />
        {/* Member Portal - Public */}
        <Route path="/home" component={MemberPortalPublic} />
        <Route
          path={["/give", "/give/recurring/:recurringGiftID"]}
          exact
          component={MemberPortalPublic}
        />

        {/* FundIt Page */}
        <Route
          path="/fundit/merchantcenter"
          component={MerchantCenterInstantAuth}
        />

        <Route path="/fundit/give" exact>
          <Redirect to="/give" />
        </Route>

        <Route path="/fundit" exact>
          <Redirect to="/onlineGivingEnrollment" />
        </Route>

        <Route
          path="/fundit/receipt/:transactionID"
          component={FunditReceiptPage}
        />
        {/* <Route path="/fundit" component={DeterminePaymentPage} /> */}
        <Route
          path="/onlineGivingEnrollment"
          component={DeterminePaymentPage}
        />

        <Route path="/enrollment" component={EnrollmentPage} />

        <Route path="/merge" component={AccountMergePage} />

        <Route path="/library" component={Library} />

        {/* /campaigns now served by /giving/campaigns */}
        <Route path="/campaigns" component={Campaigns} />

        {/* Unsubscribe Contact Page */}
        <Route
          path="/note/:noteID/unsubscribeContact"
          component={UnsubscribeContactPage}
          exact
        />

        {/* Note View */}
        <Route path="*/note/:noteID*" component={ReplyToGiveInfo} />

        <Route
          path="/giving/settings/happetools"
          component={GivingHappeTools}
        />
        <Route
          path="/giving/batchesList/print/view"
          component={GivingBatchesListPrintView}
        />
        <Route
          path="/giving/batch/:batchID/print/view"
          component={GivingBatchPrintView}
        />
        <Route
          path="/giving/fundList/print/view"
          component={GivingFundListPrintView}
        />
        <Route
          path="/giving/campaignsList/print/view"
          component={GivingCampaignsListPrintView}
        />
        <Route
          path="/giving/pledges/print/view"
          component={GivingPledgesPrintView}
        />
        <Route
          path="/giving/transactionList/print/view"
          component={GivingTransactionListPrintView}
        />
        <Route path="/giving" component={GivingPage} />

        {/* Payment Page */}
        <Route path="/give/:fundID" component={PaymentPage} />

        {/* Manually Process Payment Page for Happes */}
        <Route
          path="/manualpaymentprocess"
          component={ManualPaymentProcessing}
        />

        <Route path="/schedulednotes/view" component={ScheduledNotesReport} />

        <Route path="/register" component={FamilyRegistration} />

        <PrivateRoute
          path={["/reports/new", "/reports/:boardID", "/reports"]}
          component={SavedReports}
        />

        <PrivateRoute path="/groupsmanagement" component={GroupsManagement} />

        {/* Not exact, so /changelog/batch/:batchID lands here too — the batch
            view is inside the Change Log tab, not beside it. */}
        <PrivateRoute
          path={[
            "/householddatabase/changelog",
            "/householddatabase/duplicates",
          ]}
          component={DataCleanupPage}
        />
        <PrivateRoute
          path="/householddatabase/familyregistration"
          component={FamilyRegistrationAdminPage}
        />
        <PrivateRoute
          path="/householddatabase/statuses"
          component={HouseholdStatuses}
        />
        <PrivateRoute
          path="/householddatabase/profilefields"
          component={ExtendedFieldsPage}
        />
        <PrivateRoute
          path={["/householddatabase"]}
          component={HouseholdDatabase}
        />

        <Route
          path={["/prayer/submit"]}
          component={PublicSubmitPrayerRequest}
        />
        <PrivateRoute path={["/prayerportal"]} component={PrayerRequests} />

        <PrivateRoute path="/re-setup" component={RESetup} />

        {/* Public Pledge Page */}
        <Route path="/pledge/:campaignID" component={PledgePage} />

        {/* IIG Sacraments Page */}
        <Route path="/sacrament" component={SacramentPage} />

        {/* Ask the Member Primary Parish */}
        <Route
          path="/askthemember/relationships"
          component={AskTheMemberFamilyRelationships}
        />

        {/* Ask the Member Emergency Contact */}
        <Route
          path="/askthemember/emergencycontact"
          component={AskTheMemberEmergencyContact}
        />

        {/* Ask the Member Medical Info */}
        <Route
          path="/askthemember/medicalinfo"
          component={AskTheMemberMedicalInfo}
        />

        {/* Ask the member duplicates */}
        <Route
          path="/askTheMember/duplicate/:mergeID"
          component={AskTheMemberDuplicates}
        />

        {/* Number Management Page */}
        <Route path="/numbermanagement" component={NumberManagement} />

        {/* Smart Group Promo Splash */}
        {/* Gets the jump on NON_REACT_GROUP_URLS and displays this on a url in that list*/}
        <Route
          exact
          path="/:groupURL/people"
          component={SmartGroupPromoSplash}
        />

        {/* Import Modal Redirect - open Importing modal for legacy /:group/people/import/:importID links */}
        <Route
          path="/:groupURL/people/import/:importID"
          component={ImportRedirect}
        />

        {/* Protected Group URLs */}
        <Route exact path={NON_REACT_GROUP_URLS} component={null} />

        {/* Boards */}
        <Route
          exact
          path={[
            "*/:groupURL/board",
            "*/:groupURL/boards",
            "*/:groupURL/boards/*",
            "*/:groupURL/boards/new-board",
          ]}
          component={BoardsIntroPage}
        />

        <Route
          exact
          path={[
            "/:groupURL/board/:boardURL",
            "/:groupURL/board/:boardURL/*", // for handling both */new-board and */settings board routes
          ]}
          component={BoardPage}
        />

        <Route
          exact
          path={"/group/*/joined"}
          component={JoinGroupLandingPage}
        />

        {/* LEGACY(happe-tools): at cutover redirect to /happes/portal, keeping
            the query. The portal maps ?tool= and ?report= links, so the
            backend's /happes/tools links keep working. */}
        <Route path="/happes/tools" component={HappeTools} />
        <Route path="/happes/diosync" component={HappeToolsDiosync} />
        <Route path="/happes/portal" component={HappePortal} />
        <Route path="/happes/search/v2" component={HappeSearchV2} />

        {/* Groups */}
        {/* <Route path="/:groupURL" component={Group} /> */}
      </Switch>

      {/* Components Based on `useRouteMatch` URL matching */}
      <AnimatePresence>
        <Suspense fallback>
          {showTextingBilling && <TextingBilling key="textingBilling" />}
          {showSettingsPanels && <SettingsPanels key="settingsPanels" />}
          {newBoardMatch != null && (
            <NewBoard groupURL={newBoardMatch.params.groupURL} key="newBoard" />
          )}
          {showUserProfile ? <UserProfile key="userProfile" /> : null}
          {showEditSmartGroup ? (
            <SmartGroupFilterPanel key="smartGroupFilterPanel" />
          ) : null}
          {showAccountMergeModal ? (
            <AccountMergeModal key="accountMergeModal" />
          ) : null}
          {composer.showTemplates && <TemplatePage key="templatePage" />}
          {composer.showTemplateConfiguration && (
            <TemplateConfigurationPage key="templateConfig" />
          )}
          {showMemberToolbar ? <MemberToolbarPortal /> : null}
          {showIIGAddress ? <IIGAddressPortal /> : null}
          {showSignupAddress ? <SignupAddressPortal /> : null}
          <LegacyUnassignedGiftsButton />
          <MissingNetworkInfo />
          <ViewFiltersButtons />
          {/* TODO: Remove RecurringGivingList/PledgeUserList after deprecating classic giving*/}
          <RecurringGivingList />
          <PledgeUserList />
        </Suspense>
      </AnimatePresence>
    </Suspense>
  );
}
